/**
 * Eingelesener Altplan → Zeilen einer neuen Planversion.
 *
 * Der Altplan wird eine weitere Version unter `sige_projekte` (dieselbe
 * `project_id`, eigener Kinderbaum) — derselbe Weg wie „Neue Version
 * erstellen“, nur dass die Vorlage aus dem PDF kommt. Alle IDs vergibt der
 * Client (wie versioning.ts), damit die Rücknahme bei einem Fehler genau
 * weiß, was sie löschen muss.
 *
 * Übernommen wird WORTGETREU, was im Altplan steht: keine Katalogvorschläge,
 * kein Standard-Gewerk, keine Zeitraum-Klemmung. Einzige Umsortierung:
 * Einträge der Vorschriften-Spalte, die der Katalog als mitgeltendes Dokument
 * kennt („BaustellenO“, „Muster-MontageAnw“), wandern in die eigene Spalte —
 * dort führt die App sie seit Migration Nr. 156, gedruckt werden sie weiter
 * in Spalte 3.
 *
 * Rein, ohne Supabase: geschrieben wird in altplanSpeichern.ts.
 */

import { DEFAULT_PIKTO_KEYS } from './types';
import type { AufstellerInfo, NotrufplanInhalt, SigeProject } from './types';
import type { AltplanErgebnis, AltplanKopf, AltplanSchritt } from './altplanLesen';
import { ergaenzeDokumente, findeDokument } from '../../services/mitgeltendeDokumente';

export type DbZeile = Record<string, unknown>;

export interface AltplanAnlage {
  plan: DbZeile & { id: string };
  arbeitsschritte: (DbZeile & { id: string })[];
  bloecke: (DbZeile & { id: string })[];
}

export interface AltplanAnlageOptionen {
  projectId: string;
  versionLabel: string;
  /** Plotterbreite der Firma (vorbelegungNeuerPlan) — der Altplan hatte sein eigenes Format. */
  plotBreiteMm: number;
  aufsteller?: AufstellerInfo;
  neueId: () => string;
}

const deutsch = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/** Vorschlag für den Versionsnamen: „Altplan 5335-2 vom 25.01.2024“. */
export const altplanVersionsName = (kopf: AltplanKopf): string => {
  const nr = kopf.plan_nr.trim();
  const datum = deutsch(kopf.plan_datum);
  return ['Altplan', nr, datum ? `vom ${datum}` : ''].filter(Boolean).join(' ');
};

/**
 * Status der Version: „überarbeitet“ steht im Stempel der Vorlage, „geprüft“
 * in manchen Fassungen — sonst bleibt der Plan Entwurf. Nur `geprueft` gilt
 * beim Ablegen als Fassung (planAblage.ts).
 */
export const altplanStatus = (kopf: AltplanKopf): SigeProject['status'] => {
  if (kopf.ueberarbeitet_datum) return 'ueberarbeitet';
  if (kopf.geprueft_datum || kopf.geprueft_name.trim()) return 'geprueft';
  return 'entwurf';
};

/** Bauzeit des Plans: frühester Beginn bis spätestes Ende der Zeitbalken. */
export const altplanBauzeit = (schritte: AltplanSchritt[]): { von: string; bis: string } => {
  const daten = schritte
    .flatMap(s => [s.zeitraum_von, s.zeitraum_bis])
    .filter((d): d is string => !!d)
    .sort();
  return { von: daten[0] || '', bis: daten[daten.length - 1] || '' };
};

/** Vorschriften-Spalte aufteilen: was der Katalog als Dokument kennt, wird mitgeltendes Dokument. */
export const teileVorschriften = (liste: string[]): { vorschriften: string[]; mitgeltende_dokumente: string[] } => {
  const vorschriften: string[] = [];
  const dokumente: string[] = [];
  liste.forEach(v => {
    const d = findeDokument(v);
    if (d) dokumente.push(d.name);
    else vorschriften.push(v);
  });
  return { vorschriften, mitgeltende_dokumente: ergaenzeDokumente([], dokumente) };
};

/** Firmendaten für den Plankopf — dieselben Lücken füllt sonst das Stempelfeld (StempelfeldEditor). */
export interface AltplanProfil {
  company_name?: string | null;
  company_address?: string | null;
  company_logo?: string | null;
  settings?: { invoiceConfig?: { phone?: string | null } | null } | null;
}

export const aufstellerAusProfil = (profil: AltplanProfil | null | undefined): AufstellerInfo | undefined => {
  const a: AufstellerInfo = {};
  if (profil?.company_name?.trim()) a.firma = profil.company_name.trim();
  if (profil?.company_address?.trim()) a.adresse = profil.company_address.trim();
  const telefon = profil?.settings?.invoiceConfig?.phone;
  if (telefon?.trim()) a.telefon = telefon.trim();
  if (profil?.company_logo) a.logo_url = profil.company_logo;
  return Object.keys(a).length ? a : undefined;
};

/** Die Zeilen der neuen Version — siehe Dateikopf. */
export const baueAltplanAnlage = (e: AltplanErgebnis, opt: AltplanAnlageOptionen): AltplanAnlage => {
  const k = e.kopf;
  const planId = opt.neueId();
  const bauzeit = altplanBauzeit(e.schritte);

  const plan: AltplanAnlage['plan'] = {
    id: planId,
    project_id: opt.projectId,
    version_label: opt.versionLabel.trim() || altplanVersionsName(k),
    bauherr_name: k.bauherr_name,
    bauherr_adresse: k.bauherr_adresse,
    baumassnahme_name: k.baumassnahme_name,
    baumassnahme_adresse: k.baumassnahme_adresse,
    plan_nr: k.plan_nr,
    plan_datum: k.plan_datum || null,
    plan_inhalt: k.plan_inhalt || 'Sicherheits- und Gesundheitsschutzplan',
    status: altplanStatus(k),
    sigeko_name: k.sigeko_name,
    sigeko_mobil: k.sigeko_mobil,
    bearbeitet_name: k.bearbeitet_name,
    geprueft_name: k.geprueft_name,
    zeitraum_von: bauzeit.von || null,
    zeitraum_bis: bauzeit.bis || null,
    hauptabschnitte: e.hauptabschnitte,
    plot_breite_mm: opt.plotBreiteMm,
  };
  // Die beiden Stempeldaten liest der Generator, geschrieben hat sie die App
  // bisher nie — ob die Spalten bestehen, ist nicht belegt. Deshalb nur, wenn
  // es einen Wert gibt (und das Speichern lässt sie notfalls weg).
  if (k.bearbeitet_datum) plan.bearbeitet_datum = k.bearbeitet_datum;
  if (k.geprueft_datum) plan.geprueft_datum = k.geprueft_datum;
  if (opt.aufsteller) plan.aufsteller = opt.aufsteller;
  // Einsortiert nach dem Plandatum statt nach dem Tag des Einlesens: sonst
  // wäre ein Plan von 2024 die „neueste“ Fassung, und Handbuch, Koordinierung
  // und Einweisung (alle lesen die neueste) bezögen sich auf ihn.
  if (k.plan_datum) plan.created_at = `${k.plan_datum}T12:00:00Z`;

  const arbeitsschritte = e.schritte.map((s, i) => {
    const { vorschriften, mitgeltende_dokumente } = teileVorschriften(s.vorschriften);
    return {
      id: opt.neueId(),
      sige_projekt_id: planId,
      sortierung: i,
      bezeichnung: s.bezeichnung,
      // Bewusst leer: der Generator druckte ein Gewerk in Klammern unter die
      // Bezeichnung, der Altplan hat dort keins. Wer zuständig ist, steht wie
      // im Original in den drei Zuständigkeitsspalten.
      gewerk: '',
      hauptabschnitt: s.hauptabschnitt || null,
      zeitraum_von: s.zeitraum_von,
      zeitraum_bis: s.zeitraum_bis,
      schutzmassnahmen: s.schutzmassnahmen,
      vorschriften,
      mitgeltende_dokumente,
      hinweis_codes: s.hinweis_codes,
      hinweis_code: s.hinweis_codes.join('; ') || null,
      kritisch: false,
      koordinierungsbedarf: s.koordinierungsbedarf || null,
      zustaendig_erstellung: s.zustaendig_erstellung || null,
      zustaendig_kontrolle: s.zustaendig_kontrolle || null,
      zustaendig_instandhaltung: s.zustaendig_instandhaltung || null,
    };
  });

  const bloecke: AltplanAnlage['bloecke'] = [];
  const n = e.notruf;
  if (n.krankenhaus || n.durchgangsarzt || n.augenarzt || n.apotheke || n.ersthelfer.length) {
    const inhalt: NotrufplanInhalt = {};
    (['krankenhaus', 'durchgangsarzt', 'augenarzt', 'apotheke'] as const).forEach(key => {
      const kontakt = n[key];
      if (kontakt) inhalt[key] = kontakt;
    });
    if (n.ersthelfer.length) inhalt.ersthelfer = n.ersthelfer;
    bloecke.push({ id: opt.neueId(), sige_projekt_id: planId, typ: 'notrufplan', titel: 'Notrufplan', inhalt });
  }
  if (e.legende.length) {
    bloecke.push({ id: opt.neueId(), sige_projekt_id: planId, typ: 'legende', titel: 'Legende', inhalt: { eintraege: e.legende } });
  }
  // Die Zeichen des Altplans sind Bilder und nicht lesbar — die
  // Standardauswahl des Assistenten steht an ihrer Stelle.
  bloecke.push({
    id: opt.neueId(), sige_projekt_id: planId, typ: 'piktogrammfeld', titel: 'Gebots-/Warnzeichen',
    inhalt: { icons: [...DEFAULT_PIKTO_KEYS] },
  });

  return { plan, arbeitsschritte, bloecke };
};

/**
 * Spalten von `sige_projekte`, ohne die die Version trotzdem entstehen darf.
 * Meldet die Datenbank eine davon als unbekannt (Migration fehlt, Spalte nie
 * angelegt), wird sie weggelassen statt das Einlesen abzubrechen.
 */
export const VERZICHTBARE_PLANSPALTEN = [
  'created_at', 'version_label', 'bearbeitet_datum', 'geprueft_datum', 'aufsteller', 'hauptabschnitte',
] as const;

/** Welche verzichtbare Spalte der Fehler beanstandet — nur eine, die die Zeile auch trägt. */
export const beanstandeteSpalte = (meldung: string | null | undefined, zeile: DbZeile): string | null =>
  VERZICHTBARE_PLANSPALTEN.find(s => s in zeile && (meldung || '').includes(s)) ?? null;
